// src/FirebaseContext.js - Complete Firebase Context
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

const FirebaseContext = createContext();

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export const FirebaseProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    // Test Firebase connection
    const testConnection = () => {
      try {
        if (auth && db) {
          setIsConnected(true);
          setConnectionError(null);
          console.log('✅ Firebase connection established');
        } else {
          setIsConnected(false);
          setConnectionError('Firebase not configured');
          console.warn('⚠️ Firebase not properly configured');
        }
      } catch (error) {
        console.error('❌ Firebase connection failed:', error);
        setIsConnected(false);
        setConnectionError(error.message);
      }
    };

    testConnection();

    // Set up auth state listener only if Firebase is available
    let unsubscribe = null;
    
    if (auth) {
      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        console.log('🔥 Firebase auth state changed:', firebaseUser?.email || 'No user');
        setUser(firebaseUser);
        setLoading(false);
      }, (error) => {
        console.error('❌ Auth state change error:', error);
        setLoading(false);
      });
    } else {
      // No auth available, set loading to false
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const value = {
    user,
    loading,
    isConnected,
    connectionError,
    auth,
    db
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};